import React, { useState, useEffect } from "react";
import { Calendar, Clock, X, AlertCircle, Loader2 } from "lucide-react";

/**
 * Reschedule Event Modal Component
 * Allows users to change the event date and time
 */
const RescheduleEventModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentDate,
  currentTime,
  eventName,
  isLoading = false,
}) => {
  const [newDate, setNewDate] = useState(currentDate || "");
  const [newTime, setNewTime] = useState(currentTime || "");
  const [errors, setErrors] = useState({});

  // Format time for HTML time input (HH:MM format, remove seconds if present)
  const formatTimeForInput = (timeString) => {
    if (!timeString) return "";
    // Remove seconds if present (HH:MM:SS -> HH:MM)
    return timeString.split(":").slice(0, 2).join(":");
  };

  // Update state when modal opens or currentDate/currentTime changes
  useEffect(() => {
    if (isOpen) {
      setNewDate(currentDate || "");
      setNewTime(formatTimeForInput(currentTime || ""));
      setErrors({});
    }
  }, [isOpen, currentDate, currentTime]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};
    if (!newDate) {
      newErrors.date = "Event date is required";
    } else {
      const selectedDate = new Date(newDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = "Event date cannot be in the past";
      }
    }
    if (!newTime) {
      newErrors.time = "Event time is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format time to H:i format (HH:MM)
  const formatTimeForAPI = (timeString) => {
    if (!timeString) return "";
    // Remove seconds if present (HH:MM:SS -> HH:MM)
    return timeString.split(":").slice(0, 2).join(":");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Ensure time is in H:i format (HH:MM)
      const formattedTime = formatTimeForAPI(newTime);
      onConfirm({ date: newDate, time: formattedTime });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setNewDate(currentDate || "");
      setNewTime(formatTimeForInput(currentTime || ""));
      setErrors({});
      onClose();
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all">
        {/* Header */}
        <div className="flex items-start mb-6 space-x-4">
          <div className="flex-shrink-0 p-3 bg-blue-100 rounded-full">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="mb-1 text-xl font-bold text-gray-900">
              Reschedule Event
            </h3>
            <p className="text-sm text-gray-600">
              Update the date and time for{" "}
              <span className="font-semibold text-gray-900">
                {eventName || "this event"}
              </span>
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="flex-shrink-0 p-1 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Schedule Info */}
        <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200">
          <p className="mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Current Schedule
          </p>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                {currentDate || "Not set"}
              </span>
            </div>
            {currentTime && (
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-700">{currentTime}</span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* New Date */}
            <div>
              <label className="flex items-center mb-2 text-sm font-semibold text-gray-700">
                <Calendar className="mr-2 w-4 h-4 text-blue-600" />
                New Event Date
                <span className="ml-1 text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => {
                  setNewDate(e.target.value);
                  setErrors((prev) => ({ ...prev, date: "" }));
                }}
                min={new Date().toISOString().split("T")[0]}
                className={`w-full px-4 py-3 text-sm border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.date
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 bg-white"
                }`}
                disabled={isLoading}
              />
              {errors.date && (
                <p className="mt-1.5 flex items-center text-xs text-red-600">
                  <AlertCircle className="mr-1 w-3 h-3" />
                  {errors.date}
                </p>
              )}
            </div>

            {/* New Time */}
            <div>
              <label className="flex items-center mb-2 text-sm font-semibold text-gray-700">
                <Clock className="mr-2 w-4 h-4 text-blue-600" />
                New Event Time
                <span className="ml-1 text-red-500">*</span>
              </label>
              <input
                type="time"
                value={newTime}
                onChange={(e) => {
                  setNewTime(e.target.value);
                  setErrors((prev) => ({ ...prev, time: "" }));
                }}
                className={`w-full px-4 py-3 text-sm border rounded-xl transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.time
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 bg-white"
                }`}
                disabled={isLoading}
              />
              {errors.time && (
                <p className="mt-1.5 flex items-center text-xs text-red-600">
                  <AlertCircle className="mr-1 w-3 h-3" />
                  {errors.time}
                </p>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="p-4 mt-6 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="mb-1 font-medium">Note:</p>
                <p>
                  Rescheduling this event will notify all attendees about the
                  new date and time.
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex mt-6 space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white bg-blue-600 rounded-xl transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Rescheduling...
                </>
              ) : (
                "Reschedule Event"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RescheduleEventModal;

